import axios from "axios";
import Order from "./Order";
import store from "../../store";

const BASE_URL = "http://localhost:32567";

class SwiggyService {
  constructor() {
    this.baseUrl = BASE_URL;
    this.apiInstance = axios.create({
      baseURL: `${this.baseUrl}/api/v1/swiggy`,
      headers: {
        "Content-Type": "application/json",
        "Cache-Control": "no-cache",
      },
    });

    // Add request interceptor for dynamic authorization
    this.apiInstance.interceptors.request.use((config) => {
      config.headers.Authorization = `${new Date().getTime()}`;
      return config;
    });

    // Add response interceptor for error handling and retry mechanism
    this.apiInstance.interceptors.response.use(
      (response) => response.data,
      async (error) => {
        const originalRequest = error.config;
        // Skip retry for login API calls to prevent infinite loops
        if (originalRequest.url.includes("/login")) {
          throw error;
        }

        // If we haven't tried to retry yet
        if (!originalRequest._retry) {
          originalRequest._retry = true;
          let loginAttempts = 0;
          const maxLoginAttempts = 3;

          while (loginAttempts < maxLoginAttempts) {
            try {
              await this.login();
              // After successful login, retry the original request
              return await this.apiInstance(originalRequest);
            } catch (loginError) {
              loginAttempts++;
              if (loginAttempts === maxLoginAttempts) {
                if (!store.partnersConnectionError.includes("Swiggy")) {
                  store.partnersConnectionError.push("Swiggy");

                  setTimeout(() => {
                    loginAttempts = 0;
                    store.partnersConnectionError =
                      store.partnersConnectionError.filter(
                        (error) => error !== "Swiggy"
                      );
                  }, 10000);
                }
                throw new Error(
                  `Failed to login after ${maxLoginAttempts} attempts`
                );
              }
              // Wait for 1 second before retrying
              await new Promise((resolve) => setTimeout(resolve, 1000));
            }
          }
        }

        console.error("Swiggy API Error:", error);
        throw error.response?.data || error;
      }
    );
  }

  async login() {
    const response = await this.apiInstance.post("/login");
    return response.data;
  }

  // Order Operations
  // example order data
  // {
  //   "statusCode": 0,
  //   "statusMessage": "Success",
  //   "restaurantData": [
  //     {
  //       "restaurantId": "960313",
  //       "serverTime": "2025-06-29T20:23:34",
  //       "isOpen": true,
  //       "lastCachePollTime": 1751208785685,
  //       "batches": {},
  //       "lastOrderEventTimestamps": {
  //         "210178350655159": "2025-06-29T20:22:52"
  //       },
  //       "isServiceable": true,
  //       "stressInfo": {
  //         "stress": false
  //       },
  //       "updatedOrderIds": [
  //         "210178350655159"
  //       ],
  //       "orders": [
  //         {
  //           "last_updated_time": "2025-06-29T20:22:51",
  //           "order_id": "210178350655159",
  //           "prep_time_predicted": 5,
  //           "status": {
  //             "order_status": "ordered",
  //             "placed_status": "unplaced",
  //             "placingState": "WITH_RELAYER",
  //             "delivery_status": "unassigned",
  //             "placed_time": null,
  //             "call_partner_time": null,
  //             "ordered_time": "2025-06-29T20:22:30",
  //             "edited_status": "unedited",
  //             "edited_time": null,
  //             "food_prep_time": null,
  //             "cancelled_time": null,
  //             "order_handover_window": 180,
  //             "early_mfr_time": 60,
  //             "hand_over_delayed": false
  //           },
  //           "current_order_action": "nothing",
  //           "delivery_boy": {},
  //           "customer_comment": "",
  //           "customer_area": "Keshav Kunj 1 Society",
  //           "customer_distance": 0.3,
  //           "restaurant_details": {
  //             "restaurant_lat": "19.0664375",
  //             "restaurant_lng": "73.0060625",
  //             "categories": []
  //           },
  //           "cart": {
  //             "charges": {
  //               "packing_charge": 0
  //             },
  //             "items": [
  //               {
  //                 "item_id": "151316226",
  //                 "quantity": 1,
  //                 "name": "Butter chapati",
  //                 "restaurant_discount_hit": 0,
  //                 "final_sub_total": 33,
  //                 "sub_total": 33,
  //                 "total": 33,
  //                 "category": "Bread",
  //                 "sub_category": "nota",
  //                 "charges": {
  //                   "Service Charges": "0.00",
  //                   "GST": "1.65",
  //                   "Vat": "0",
  //                   "Service Tax": "0"
  //                 },
  //                 "tax_expressions": {
  //                   "GST_inclusive": false,
  //                   "Service Charges": "0.00",
  //                   "Vat": "0",
  //                   "Service Tax": "0"
  //                 },
  //                 "addons": [],
  //                 "variants": "",
  //                 "newAddons": [],
  //                 "newVariants": [],
  //                 "is_oos": false,
  //                 "is_veg": "1",
  //                 "reward_type": null,
  //                 "free_quantity": 0
  //               }
  //             ]
  //           },
  //           "restaurant_taxation_type": "GST",
  //           "GST_details": {
  //             "cartCGST": 0.824999988079071,
  //             "cartIGST": 0,
  //             "cartSGST": 0.824999988079071,
  //             "itemCGST": 0.824999988079071,
  //             "itemIGST": 0,
  //             "packagingCGST": 0,
  //             "packagingSGST": 0,
  //             "restaurant_liable_gst": 0,
  //             "swiggy_liable_gst": 1.65
  //           },
  //           "gst": 0,
  //           "serviceCharge": 0,
  //           "spending": 0,
  //           "tax": 0,
  //           "discount": 0,
  //           "bill": 34.649996,
  //           "restaurant_trade_discount": null,
  //           "total_restaurant_discount": 0,
  //           "type": "regular",
  //           "cafe_data": {
  //             "restaurant_type": null
  //           },
  //           "is_assured": false,
  //           "discount_descriptions": [],
  //           "delivery": {
  //             "promiseId": "5edf8acb-9003-42f9-9695-669b3615ed1a"
  //           },
  //           "outlier_meta": {
  //             "has_complaint_outliers": false
  //           },
  //           "final_gp_price": 0,
  //           "customer": {
  //             "special_instructions": {
  //               "si_opted_in": false,
  //               "si_cx_instruction": "",
  //               "si_status": "SENT"
  //             },
  //             "customer_id": "175624939",
  //             "customer_lat": "19.0663756",
  //             "customer_lng": "73.0060383",
  //             "customer_name": "Ash"
  //           },
  //           "prep_time_details": {
  //             "predicted_prep_time": 5.116666666666666,
  //             "max_increase_threshold": 5,
  //             "max_decrease_threshold": 5
  //           },
  //           "cutleryOptedIn": false,
  //           "order_expiry_time": "2025-06-30T01:22:51",
  //           "is_prep_timer_enabled": true,
  //           "isMFRAccuracyCalculated": false,
  //           "rest_extra_prep_time": null,
  //           "promise_prep_time": null,
  //           "foodHandoverTimeSec": 180,
  //           "onDemandEnricherFlags": {
  //             "shouldQueryOutliers": false
  //           }
  //         }
  //       ],
  //       "popOrders": []
  //     }
  //   ],
  //   "force_update": 46,
  //   "optional_update": 46,
  //   "battery_status_time": 10,
  //   "config": {
  //     "pollingInterval": "30000"
  //   }
  // }
  async getOrders() {
    try {
      if (store.partnersConnectionError.includes("Swiggy")) {
        throw new Error("Swiggy API: Connection error");
      }

      const response = await this.apiInstance.get("/orders");

      // Check if response and restaurantData exist
      if (
        !response ||
        !response.restaurantData ||
        !Array.isArray(response.restaurantData)
      ) {
        console.warn(
          "Swiggy API: Invalid response format or missing restaurantData"
        );
        return [];
      }

      // Extract all orders from all restaurants in restaurantData
      const allOrders = response.restaurantData.reduce((orders, restaurant) => {
        // Check if restaurant and orders array exists
        if (restaurant && Array.isArray(restaurant.orders)) {
          return orders.concat(restaurant.orders);
        }
        return orders;
      }, []);

      // Transform valid orders using the Order class
      return allOrders
        .filter((orderData) => orderData && orderData.order_id) // Only process valid orders
        .map((orderData) => new Order(orderData, "swiggy", this));
    } catch (error) {
      console.error("Swiggy API: Error fetching orders:", error);
      return [];
    }
  }

  async getOrderDetails(orderId) {
    const orderData = await this.apiInstance.get(
      `/orders/details?order_id=${orderId}`
    );
    return new Order(orderData, "swiggy", this);
  }

  // These methods are now internal and called by Order class
  async acceptOrder(orderId, deliveryTime = "30") {
    return this.apiInstance.post(
      `/orders/accept?order_id=${orderId}&prep_time=${deliveryTime}`
    );
  }

  async markOrderReady(orderId) {
    return this.apiInstance.post(`/orders/ready?order_id=${orderId}`);
  }

  async rejectOrder(orderId) {
    return this.apiInstance.post(`/orders/reject?order_id=${orderId}`);
  }

  async getOrderHistory() {
    const orders = await this.apiInstance.get("/orderHistory");
    return orders.map((orderData) => new Order(orderData, "swiggy", this));
  }

  // Item Operations
  async getItems() {
    return this.apiInstance.get("/items");
  }

  async markItemInStock(itemId) {
    return this.apiInstance.post(`/items/instock?item_id=${itemId}`);
  }

  async markItemOutOfStock(itemId) {
    return this.apiInstance.post(`/items/outofstock?item_id=${itemId}`);
  }

  async updateItemPrice(itemId, price) {
    return this.apiInstance.post(`/item/price/${itemId}`, { price });
  }

  async deleteItem(itemId) {
    return this.apiInstance.delete(`/items/delete?id=${itemId}`);
  }

  // Category Operations
  async markCategoryInStock(categoryId) {
    return this.apiInstance.post(`/category/instock?category_id=${categoryId}`);
  }

  async markCategoryOutOfStock(categoryId) {
    return this.apiInstance.post(
      `/category/outofstock?category_id=${categoryId}`
    );
  }

  // Restaurant Operations
  async acceptOrderForRestaurant(restaurantId, orderId) {
    return this.apiInstance.post(
      `/orders/accept/${restaurantId}?order_id=${orderId}`
    );
  }

  async markOrderReadyForRestaurant(restaurantId, orderId) {
    return this.apiInstance.post(
      `/orders/ready/${restaurantId}?order_id=${orderId}`
    );
  }

  async getItemsForRestaurant(restaurantId) {
    return this.apiInstance.get(`/items/${restaurantId}`);
  }

  // Outlet Operations
  async closeOutlet() {
    return this.apiInstance.post("/close_outlet");
  }

  async closeOutletForRestaurant(restaurantId) {
    return this.apiInstance.post(`/close_outlet/${restaurantId}`);
  }
}

export const swiggyService = new SwiggyService();
